import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { useCommandPalette } from '../hooks/useCommandPalette';
import TopBar from './TopBar';
import ContextualSidebar from './ContextualSidebar';
import CommandPalette from './CommandPalette';
import ModuleIframe from './ModuleIframe';
import WorkspaceRail from './WorkspaceRail';
import WorkspaceHub from './WorkspaceHub';
import ScreenshotCapture from '../components/ScreenshotCapture';
import RuntimeUpdateBanner from '../components/RuntimeUpdateBanner';
import { X, Link2, Eye } from 'lucide-react';
import { derivePageId } from '../utils/pageId';
import './RuntimeShell.css';

function ImpersonationBanner() {
    const [name, setName] = useState(null);
    useEffect(() => {
        setName(localStorage.getItem('crm_impersonating'));
    }, []);
    if (!name) return null;
    const isViewAs = name?.startsWith('View As:');
    const endImpersonation = () => {
        localStorage.removeItem('crm_token');
        localStorage.removeItem('crm_impersonating');
        window.close();
    };
    return (
        <div className={`${isViewAs ? 'bg-indigo-600' : 'bg-blue-600'} text-white text-center py-2 px-4 text-xs font-medium flex items-center justify-center gap-3`} data-testid="impersonation-banner">
            <div className="flex items-center gap-2">
                <Eye className="w-3.5 h-3.5" />
                <span>{isViewAs ? (
                    <><strong>{name.replace('View As: ', '')}</strong> — You are previewing this user's view.</>
                ) : (
                    <>Viewing as <strong>{name}</strong> (impersonation mode)</>
                )}</span>
            </div>
            <button onClick={endImpersonation} className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded text-xs font-semibold transition-colors">
                {isViewAs ? 'Exit Preview' : 'End Session'}
            </button>
        </div>
    );
}

function ShellContent({ children }) {
    const { user } = useAuth();
    const location = useLocation();
    const { currentWorkspace, isInstalled, getHealthStatus } = useWorkspace();
    const { isOpen: isPaletteOpen, openPalette, closePalette } = useCommandPalette();
    // useModuleInstallation is now called once in WorkspaceProvider (Phase 2)
    const [mobileOpen, setMobileOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [hubOpen, setHubOpen] = useState(false);

    // Onboarding redirect (preserved from AppLayout)
    useEffect(() => {
        if (user && user.onboarding_completed === false &&
            !location.pathname.startsWith('/onboarding') &&
            !location.pathname.startsWith('/settings') &&
            !location.pathname.startsWith('/admin')) {
            window.location.href = '/onboarding';
        }
    }, [user, location.pathname]);

    // SSO event alert (preserved from AppLayout)
    const [ssoAlert, setSsoAlert] = useState(null);
    useEffect(() => {
        const ssoEvent = sessionStorage.getItem('sso_event');
        if (ssoEvent) {
            sessionStorage.removeItem('sso_event');
            if (ssoEvent === 'linked') {
                setSsoAlert({
                    icon: Link2,
                    title: 'Multiplai account linked',
                    message: 'Your Multiplai account has been linked to your existing account.',
                });
            }
        }
    }, []);

    // Close mobile sidebar on route change
    useEffect(() => {
        const handlePopState = () => setMobileOpen(false);
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    // Close mobile sidebar whenever the pathname changes (deep-link, palette navigation, etc.)
    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    const isModuleIframe = currentWorkspace.type === 'module-iframe';

    // Custom modules (type 'module-native') mount their own app into the main
    // column via ModuleHost, so they need the same clipped flex column an iframe
    // gets: a module whose UI is a tree panel beside a document (the wiki) owns its
    // internal scroll and must not also scroll as a page. Keyed off the workspace
    // TYPE rather than a /^\/m\// path regex so it needs no per-module registry —
    // every installed custom module gets the layout by virtue of being one.
    const isModuleNative = currentWorkspace.type === 'module-native';

    // Full-height form pages (workflow canvas, builder, page designer, forwarder/
    // redirect/condition editors) render inside the shell — rail, sidebar, and
    // topbar stay visible — but the main column must become a clipped flex column
    // (same layout module iframes use) instead of the padded page scrollport:
    // these pages own their internal scroll/pan and must never scroll as a page.
    // Do NOT let the pages escape with position:fixed/absolute overlays instead —
    // the rail (z-index 60) paints over them and clips their left edge.
    const isFullscreenPage = [
        /^\/forms\/[^/]+\/(workflow|edit|forwarders|redirects)$/,
        /^\/forms\/[^/]+\/conditions\/[^/]+$/,
        /^\/forms\/[^/]+\/steps\/[^/]+\/design$/,
        // The whole inbox family. Every one of these pages already roots itself
        // in `flex flex-col h-full` and owns an internal `overflow-y-auto`
        // region — but `h-full` resolves to `auto` against the padded page
        // scrollport, so each one grew to its content height and its internal
        // scroll region never scrolled. In the message list that also stranded
        // the infinite-scroll sentinel: nothing ever scrolled past it, so the
        // next page never loaded and the list stopped at 40 messages.
        /^\/inbox$/,
        // Compose, both lanes. `ComposeSurface` is a bounded flex column: a
        // fixed action bar, ONE internal scroll region, a fixed footer, and
        // hairline rows that run edge to edge. Off this list the main column is
        // a padded page scrollport, so `h-full` resolves to `auto`, the panel
        // grows to its content height inside a gutter, and the footer scrolls
        // away — which is exactly the "not full width, wrong layout" the design
        // rebuild fixes.
        /^\/inbox\/compose$/,
        /^\/inbox\/[^/]+\/compose$/,
        // The thread readers, both lanes. `ThreadReaderSurface` is the same
        // bounded flex column as compose: a fixed toolbar, ONE internal scroll
        // region, a fixed footer, and hairline message rules that run edge to
        // edge. These were deliberately OFF this list while the reader "read as
        // a document" and owned no scroll region of its own — it owns one now,
        // so off the list `h-full` resolves to `auto` against the padded page
        // scrollport, the toolbar and footer stop being reachable on a long
        // thread, and the auto-scroll-to-newest finds nothing to scroll.
        /^\/inbox\/email\/[^/]+$/,
        // `:inboxId/:conversationId` — the two SIBLING three-segment routes are
        // excluded by name. `/inbox/:inboxId/settings` is an ordinary padded
        // settings form with no internal scroll region, and handing it a
        // clipped flex column would cut its content off with nothing to
        // scroll; `/compose` is already on this list two lines up.
        /^\/inbox\/[^/]+\/(?!compose$|settings$)[^/]+$/,
        // The AI Assistant is the same shape as the inbox: a sidebar plus a
        // message column that owns its own scroll region. It was reaching for
        // full height with `h-[calc(100vh-8rem)]` — an arithmetic guess at the
        // chrome above it, which is wrong the moment a banner renders
        // (impersonation, runtime update) and leaves the composer off-screen.
        // Opting in here lets the shell give it a bounded flex column instead.
        /^\/ai-assistant$/,
        // The calendar family (#676). The grid, the full event view and the
        // editor are bounded flex columns — a fixed toolbar over a rail and a
        // grid (or a form and a side panel) that each own ONE scroll region.
        // Off this list `h-full` resolves to `auto` against the padded page
        // scrollport and the 24-hour grid grows to its full 1344px height
        // instead of scrolling inside the page.
        /^\/calendar$/,
        /^\/calendar\/new$/,
        /^\/calendar\/event\/[^/]+(\/edit)?$/,
        // List Management (#767). The prototype layout is a fixed header strip
        // of tabs over a 232px source menu and a content column that owns ONE
        // scroll region (the wide tables scroll inside their card). Off this
        // list the page would grow to its content height inside the padded
        // scrollport and the tab strip would scroll away.
        /^\/list-management\/?$/,
        // Ask the Wiki — the ONLY Wiki route here. Its composer stays fixed
        // under a thread that owns ONE scroll region, so it needs a bounded
        // column; `WikiFrame bounded` re-applies the shell's 12/16/24px
        // padding and 1600px container itself. Every other Wiki route renders
        // in the padded page scrollport exactly like the Dashboard (Owen,
        // 2026-10-06).
        /^\/wiki\/ask$/,
    ].some((re) => re.test(location.pathname));
    const isFullHeight = isModuleIframe || isModuleNative || isFullscreenPage;

    return (
        <div
            className="runtime-shell-root shell-motion"
            style={{ '--ws': currentWorkspace.hue || 'var(--ink-1)' }}
        >
            <ImpersonationBanner />
            <RuntimeUpdateBanner />
            <div className="runtime-shell-body">
                <WorkspaceRail
                    onOpenHub={() => setHubOpen(true)}
                    getHealthStatus={getHealthStatus}
                />

                {/* Sidebar column — in-flow at ≥768px, overlay only on true mobile.
                    `inert` (React 19) removes hidden children from tab order + a11y tree. */}
                <div
                    className={`shell-sidebar-slot ${sidebarCollapsed ? 'collapsed' : ''} ${mobileOpen ? 'open' : ''}`}
                    inert={sidebarCollapsed ? '' : undefined}
                >
                    <ContextualSidebar onNavigate={() => setMobileOpen(false)} />
                </div>

                {/* Mobile scrim */}
                {mobileOpen && (
                    <div
                        className="fixed inset-0 bg-black/40 z-40 md:hidden"
                        onClick={() => setMobileOpen(false)}
                        data-testid="sidebar-overlay"
                    />
                )}

                <div className="runtime-shell-content flex-1 flex flex-col overflow-hidden min-w-0">
                    <TopBar
                        onOpenPalette={openPalette}
                        onMenuToggle={() => setMobileOpen(prev => !prev)}
                        onOpenHub={() => setHubOpen(true)}
                        onToggleSidebar={() => setSidebarCollapsed(v => !v)}
                        sidebarCollapsed={sidebarCollapsed}
                        getHealthStatus={getHealthStatus}
                    />
                    {/* `shell-main-module` opts this column into the full-height flex
                        layout in RuntimeShell.css. Module iframes and fullscreen form
                        pages carry it; other native pages must not — they own the page
                        scrollport via `overflow-y-auto`. */}
                    <main className={`flex-1 ${isFullHeight ? 'shell-main-module overflow-hidden' : 'overflow-y-auto'}`}>
                        <div className={`${isFullHeight ? 'flex-1 flex flex-col min-h-0' : 'p-3 sm:p-4 md:p-6'}`} data-host-page data-page-id={derivePageId(location.pathname)}>
                            {ssoAlert && (
                                <div className="mb-4 bg-surface border border-line rounded-xl p-4 flex items-start gap-3 shadow-sm animate-fade-in">
                                    <div className="flex-shrink-0 w-9 h-9 bg-ink-1 rounded-lg flex items-center justify-center">
                                        <ssoAlert.icon className="w-4.5 h-4.5 text-canvas" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-semibold text-ink-1" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>{ssoAlert.title}</p>
                                        <p className="text-sm text-ink-2 mt-0.5">{ssoAlert.message}</p>
                                    </div>
                                    <button onClick={() => setSsoAlert(null)} className="flex-shrink-0 p-1 rounded-md text-ink-label hover:text-ink-1 hover:bg-black/5 transition-colors">
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            )}
                            {isModuleIframe ? (
                                <ModuleIframe workspace={currentWorkspace} getHealthStatus={getHealthStatus} />
                            ) : (
                                children
                            )}
                        </div>
                    </main>
                    <ScreenshotCapture />
                </div>
            </div>
            {hubOpen && (
                <WorkspaceHub
                    onClose={() => setHubOpen(false)}
                    isInstalled={isInstalled}
                    getHealthStatus={getHealthStatus}
                />
            )}
            {isPaletteOpen && (
                <CommandPalette
                    onClose={closePalette}
                    onOpenHub={() => setHubOpen(true)}
                />
            )}
        </div>
    );
}

export default function RuntimeShell({ children }) {
    return <ShellContent>{children}</ShellContent>;
}
